import { playlistEntryKey } from './playlist-groups.js';
import type { ChainLink, ChainView, PlaylistEntry } from './runs-api.js';

// #128: „Meine Tests" nach E2E-Prozess gruppieren — die reinen Regeln der dritten Ansicht. Die
// Komponente (pages/my-tests/chain-groups.tsx) liest nur hieraus; dieselbe Trennung wie zwischen
// playlist.tsx und playlist-groups.ts (#83-Lehre: eine Quelle, mehrere Leser).

// Fortschritt in der Gruppenüberschrift: „3/7 bestanden". Gezählt wird der LETZTE Lauf je Glied
// (A2: keine Lauf-Historie) — dieselbe Sicht, die auch die Kettenleiste zeigt.
export function chainProgress(view: ChainView): { passed: number; total: number } {
  const passed = view.links.filter((l) => l.last_run?.result === 'successful').length;
  return { passed, total: view.links.length };
}

// Der Kettenstatus (offene Frage 7, Default angewandt). Bewusst NICHT „blockiert"/„wartet" — beide
// Wörter sind vergeben (global-constraints, Naming): „Blockiert" ist ein Lauf-Ergebnis, „wartet"
// der Zustand eines Glieds. Die Kette selbst „liegt bei …", „steht bei Schritt …" oder ist
// „erledigt".
export type ChainStatus =
  | { kind: 'done' }
  // Das erste nicht bestandene Glied hat einen Fehler- oder Blockiert-Lauf: hier geht es erst
  // weiter, wenn jemand etwas repariert.
  | { kind: 'stopped'; link: ChainLink }
  // Das erste nicht bestandene Glied ist nie gelaufen oder steht auf „Wiederholen": es liegt
  // schlicht beim zuständigen Tester.
  | { kind: 'at'; link: ChainLink };

export function chainStatus(view: ChainView): ChainStatus {
  // Server-Reihenfolge = Sequenz (buildChainViews sortiert); „erstes" heißt hier also „frühester
  // Schritt", nicht `sequence` ± 1 (Lücken durch abgewählte Glieder, s. architektur.md).
  const current = view.links.find((l) => l.last_run?.result !== 'successful');
  if (!current) return { kind: 'done' };
  const result = current.last_run?.result;
  if (result === 'error' || result === 'blocked') return { kind: 'stopped', link: current };
  return { kind: 'at', link: current };
}

// Schlüssel eines Kettenglieds im Sinne der Playlist: das Paar (Testfall, Kette). Über
// `playlistEntryKey`, damit „eigen" exakt dieselbe Identität meint wie Auswahl und React-`key` —
// KEINE zweite Schlüsselformel.
export function chainLinkKey(view: ChainView, link: ChainLink): string {
  return playlistEntryKey({ test_case_id: link.test_case_id, e2e_id: view.e2e_id });
}

export interface ChainGroup {
  view: ChainView;
  // Schlüssel (playlistEntryKey) der Glieder, die in der eigenen Playlist stehen. Alle anderen
  // Glieder der Kette gehören anderen Testern und werden nur gezeigt, nicht bedient.
  ownKeys: Set<string>;
}

export function groupByChain(
  entries: PlaylistEntry[],
  views: ChainView[],
): { chains: ChainGroup[]; noChain: PlaylistEntry[] } {
  const known = new Set(views.map((v) => v.e2e_id));
  const chains = views.map((view) => ({
    view,
    ownKeys: new Set(
      entries.filter((e) => e.e2e_id === view.e2e_id).map((e) => playlistEntryKey(e)),
    ),
  }));
  // Kettenlos = ohne e2e_id. Zusätzlich landet hier ein Eintrag, dessen Kette der Server nicht
  // mitgeliefert hat: lieber unter „Ohne E2E-Prozess" sichtbar als still verschluckt (#78-Muster —
  // keine stille Kappung). Im Normalfall tritt das nicht auf, weil die Kettenliste genau aus den
  // e2e_ids dieser Playlist abgefragt wird.
  const noChain = entries.filter((e) => e.e2e_id === null || !known.has(e.e2e_id));
  return { chains, noChain };
}
